import axios, { AxiosError } from "axios";
import { useState, FormEvent } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import ValidatorsHelper from "../../../helpers/validatorsHelper";
import apiNet from "../../../newApi/apiNet";
import { ButtonSize, ButtonType, ButtonVariant } from "../../../types/button";
import { InputType } from "../../../types/input";
import Button from "../../buttons/Button";
import ButtonLink from "../../buttons/ButtonLink";
import FormInput from "../../inputs/FormInput";
import CircleLoader from "../../loaders/CircleLoader";
import { XCircleIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import FormWrapper from "../../forms/FormWrapper.tsx";
import { BadRequestException } from "../../../exceptions/badRequestException.ts";

export const RecoveryPassword = () => {
    const _recaptchaService = useGoogleReCaptcha();
    const [email, setEmail] = useState("");
    const [isEmailValid, setIsEmailValid] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isFinishedSuccess, setIsFinishedSuccess] = useState<undefined | boolean>(undefined);

    const handleSubmitEvent = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (isEmailValid && _recaptchaService.executeRecaptcha) {
            try {
                const recaptchaToken = await _recaptchaService.executeRecaptcha('recoveryPassword');
                setIsLoading(true);
                const response = await apiNet.recoverPassword(recaptchaToken, email);
                if (response.status === 204) {
                    setIsFinishedSuccess(true);
                }
            } catch (e) {
                const error = e as Error | AxiosError;
                if (axios.isAxiosError(error) && error.response && error.response.status === 400) {
                    throw BadRequestException("INVALID_CREDENTIALS", "Invalid credentials");
                }
                setIsFinishedSuccess(false);
                throw new Error(axios.isAxiosError(error) && error.response && error.response.data ? error.response.data : "Something went wrong...");
            } finally {
                setIsLoading(false);
            }
        } else {
            setShowErrors(true);
        }
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        setIsEmailValid(ValidatorsHelper.isEmailValid(e.target.value));
    }
    return (
        <div>
            {isFinishedSuccess === undefined &&
                <FormWrapper>
                    <div>
                        <h1 className={"text-3xl font-bold"}>Odzyskiwanie hasła</h1>
                        <p className={"mt-5 text-neutral-300"}>Wpisz adres email na którym zarejestrowane jest konto,
                            aby odzyskać hasło.</p>
                        <form action="/recoveryPassword" className={"w-full mt-10 text-left"}
                            onSubmit={(e) => handleSubmitEvent(e)}
                        >
                            <FormInput
                                label={"Email"}
                                name={"email"}
                                onInputChange={handleInputChange}
                                type={InputType.EMAIL}
                                value={email}
                                placeholder={"Wpisz adres email"}
                                className={"py-2 placeholder:text-neutral-400 placeholder:font-light bg-neutral-700 ring-offset-neutral-900"} />
                            {showErrors && !isEmailValid &&
                                <p className={"text-red-500 mt-2"}>Adres email jest nie poprawny</p>
                            }
                            <p className={"text-xs text-neutral-400 my-7 mt-5"}>Ta strona korzysta z usługi reCAPTCHA.
                                Usługę
                                reguluje <ButtonLink to={"https://policies.google.com/terms"}
                                    variant={ButtonVariant.SECONDARY}
                                    targetType={"_blank"}
                                    className={"text-xs pl-0 pr-0 pt-0 pb-0"}>regulamin
                                    usługi</ButtonLink> Google.</p>
                            <Button
                                type={ButtonType.SUBMIT}
                                size={ButtonSize.BASE}
                                disabled={isLoading}
                                variant={ButtonVariant.PRIMARY}
                                className={"mx-auto block w-full py-1.5 text-lg min-h-10 ring-offset-neutral-900"}>
                                {isLoading ? <CircleLoader className={"w-6 mx-auto"} /> : "Wyslij"}
                            </Button>
                        </form>
                    </div>
                </FormWrapper>
            }
            {isFinishedSuccess === false &&
                <FormWrapper>
                    <XCircleIcon className={"w-16 h-16 text-red-400 mx-auto mb-4 rounded-full"} />
                    <h1 className={"text-3xl mb-4"}>Niepowodzenie</h1>
                    <p className={"text-neutral-400"}>Twoje żądanie o zmianę hasła nie powiodło się. Spróbuj ponownie za
                        kilka minut.</p>
                </FormWrapper>
            }
            {isFinishedSuccess === true &&
                <FormWrapper>
                    <CheckCircleIcon className={"w-16 h-16 text-green-400 mx-auto mb-4 rounded-full"} />
                    <h1 className={"text-3xl mb-4"}>Sukces</h1>
                    <p className={"text-neutral-400"}>Twoje żądanie o zmianę hasła zakończyło się powodzeniem. Jeżeli
                        adres email jest prawidłowy, otrzymasz na niego link do zmiany hasła. Kliknij w niego i postępuj
                        zgodnie z instrukcjami.</p>
                </FormWrapper>
            }
        </div>
    )
}
